/**
 * @file superadmin-model.component.ts
 * @description
 * This component handles the creation and editing of superadmin users through a dialog form.
 * It allows the superadmin to add or update user details within the Superadmin Panel.
 *
 * The component includes the following:
 * 1. A form with input fields for superadmin user details, including:
 *    - Email (Required, Unique)
 *    - Password (Required for new users)
 *    - Phone Number
 *    - First Name
 *    - Middle Name
 *    - Last Name
 * 2. Form validation to ensure all required fields are properly filled.
 * 3. A submit action that interacts with the backend service to create or update a superadmin user.
 * 4. Error handling and user feedback (success/error messages).
 * 5. Dialog functionality for opening and closing the form modal.
 *
 * @dependencies:
 * - Superadmin Service for handling user creation and updates.
 * - Bootstrap Modal for managing the dialog form.
 * - Angular Reactive Forms (`@angular/forms`) for form handling and validation.
 * - Angular Bootstrap (`ng-bootstrap`) for modal management.
 * - Angular Material Snackbar (`MatSnackBar`) for displaying success and error messages.
 * @param {MatSnackBar} snackBar - Used to display notifications for success and error messages.
 */

import { Component, Input, Output } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Superadmin, SuperadminEditRequest, SuperadminRequest } from '../../../models/superadmin.model';
import { SuperadminService } from '../../../shared/services/superadminusers/superadmin.service';
import { FormValidation } from '../../../shared/services/utils/form-validation.service';
import { PhoneMaskDirective } from '../../../shared/directive/phone-mask.directive';
import { showNotification } from '../../../consts/utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SeoService } from '../../../shared/services/seo.service';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';


@Component({
  selector: 'app-superadmin-model',
  imports: [ReactiveFormsModule, PhoneMaskDirective, CommonModule, LoaderComponent],
  templateUrl: './superadmin-model.component.html',
  styleUrl: './superadmin-model.component.scss'
})

export class SuperadminModelComponent {

  @Input() user: Superadmin;
  @Output() refreshTable: any;

  public show: boolean = true;
  public showConfirm: boolean = true;

  form: UntypedFormGroup;
  public validate = false;
  isLoading = false; // Flag to control loader visibility

  constructor(private modal: NgbModal,
    private _formBuilder: UntypedFormBuilder,
    private _superadminService: SuperadminService,
    public formValidation: FormValidation,
    private snackBar: MatSnackBar,
    private seo: SeoService
  ) {
    /**
        * Sets up SEO meta tags using SeoService
        * This helps in optimizing the page for search engines and social media previews.
    */
    this.seo.setTags({
      title: 'Superadmin Form Dialog',
      titleSuffix: '- Shining On Safety',
      description: 'Shining On Safety',
      keywords: 'Shining On Safety'
    });
  }

  ngOnInit(): void {
    this.form = this._formBuilder.group({
      firstName: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(1),
          ]),
        ],
      ],
      middleName: [        '',        [          Validators.compose([ Validators.maxLength(50) ]),        ],
      ],
      lastName: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(1),
          ]),
        ],
      ],
      email: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(4),
          ]),
        ],
      ],
      phone: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(4),
          ]),
        ],
      ],
      password: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(50),
            Validators.minLength(8),
          ]),
        ],
      ],
    },);

    this.form.valueChanges.subscribe(values => {
      ['firstName', 'middleName', 'lastName'].forEach(field => {
        let value = this.form.get(field)?.value;
        if (value) {
          this.form.get(field)?.setValue(value.charAt(0).toUpperCase() + value.slice(1), { emitEvent: false });
        }
      });
    });


    if (this.user) {
      this.form.get("firstName")?.setValue(this.user.firstName);
      this.form.get("middleName")?.setValue(this.user.middleName);
      this.form.get("lastName")?.setValue(this.user.lastName);
      this.form.get("email")?.setValue(this.user.email);
      this.form.get("password")?.setValue("password");
      this.form.get("phone")?.setValue(this.user.phone);
    }
  }

  /**
  *  API call for Add superadmin
  */
  register() {
    this.validate = true;
    if (this.form.invalid) {
      showNotification(this.snackBar, 'Please check the form fields', 'error');
      return;
    }
    const user: SuperadminRequest = {
      firstName: this.form.get('firstName')?.value,
      middleName: this.form.get('middleName')?.value,
      lastName: this.form.get('lastName')?.value,
      email: this.form.get('email')?.value,
      password: this.form.get('password')?.value,
      phone: this.form.get('phone')?.value,
    };
    this.isLoading = true; // Show loader
    this._superadminService.register(user).subscribe({
      next: (response) => {
        this.isLoading = false; // Hide loader after success
        showNotification(this.snackBar, response.message, 'success');
        this.close();
        this.refreshTable();
      },
      error: (error) => {
        this.isLoading = false; // Hide loader after fail
        showNotification(this.snackBar, error.message, 'error');
      },
    })
  }

  /**
  *  API call for edit superadmin
  */
  editSuperAdmin() {
    if (this.form.invalid) {
      showNotification(this.snackBar, 'Please check the form fields', 'error');
      return;
    }
    const profileEditInformation: SuperadminEditRequest = {
      firstName: this.form.get('firstName')?.value,
      middleName: this.form.get('middleName')?.value,
      lastName: this.form.get('lastName')?.value,
      email: this.form.get('email')?.value,
      phone: this.form.get('phone')?.value,
    }
    this.isLoading = true; // Show loader
    this._superadminService.updateSuperadmin(this.user._id, profileEditInformation).subscribe({
      next: (response) => {
        //this.isLoading = false; // Hide loader after success
        showNotification(this.snackBar, response.message, 'success');
        this.close();
        this.refreshTable();
      },
      error: (error) => {
       // this.isLoading = false; // Hide loader after fail
        showNotification(this.snackBar, error.message, 'error');
      },
    })
  }

  /**
  *  Dismiss Dialog
  */
  close() {
    this.modal.dismissAll();
  }
}
